import React from 'react';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Asset } from 'contentful';

import { IAffiliateHeroFields } from '../../../../types/contentful';

import { getImageData } from '../../../utils/contentful';

import { CFColorTokenProp } from '../../../theme/tokens/contentful-color';
import { BlockContainer, Column, Layout } from '../../Layout';
import { LayoutContainer } from '../../Layout/styles';

import {
  NoOverflowBox,
  MobileImage,
  HeroText,
  AffiliatePhoneNumberWrapper,
  InlineAnchor
} from './styles';

import { ParagraphWithLineBreaks } from '../../RichText/styles';
import {
  HomepagePhoneNumberWrapper,
  SubheadText,
  MobileVersion,
  DesktopBlockContainer,
  DesktopHeroLayout
} from '../../HomepageHero/styles';

import { Box } from '../../Box';
import RichText from '../../RichText';
import { EmbeddedInlineEntryNode } from '../../RichText/Nodes';
import { Text } from '../../Text';
import { Flexbox } from '../../Flexbox';
import CtaWidget from '../../CtaWidget';
import {
  ctaWidgetEnum,
  DEFAULT_LABEL_V1,
  DEFAULT_PLACEHOLDER_V1
} from '../../CtaWidget/shared';

export function AffiliateHero({
  headingText,
  subheadingText,
  image,
  desktopImage,
  disclaimer,
  useLightText,
  ctaColor,
  ctaHoverColor,
  ctaDesktopColor,
  ctaDesktopHoverColor
}: IAffiliateHeroFields) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <ParagraphWithLineBreaks
          as="p"
          fontSize={{
            0: '2xs',
            tablet: 's'
          }}
          fontWeight="400"
          colour={{
            0: 'defaultText',
            desktop: useLightText ? 'white' : 'defaultText'
          }}
        >
          {children}
        </ParagraphWithLineBreaks>
      );
    },
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour={{
          0: 'defaultText',
          tablet: 'defaultText',
          desktop: useLightText ? 'white' : 'defaultText'
        }}
      >
        {children}
      </EmbeddedInlineEntryNode>
    ),
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineAnchor href={node.data.uri} useLightText={useLightText || false}>
        {children}
      </InlineAnchor>
    )
  };

  return (
    <>
      <MobileVersion data-blockid="affiliates-hero-mobile">
        <BlockContainer
          as="section"
          marginTop={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
          marginBottom={{
            0: 'xs',
            tablet: 'blockMarginY_tablet',
            desktop: 'm'
          }}
          revealOnScroll={false}
          data-blockid="overlay-hero"
        >
          <LayoutContainer>
            <NoOverflowBox
              borderRadius={{ 0: 'block', desktop: 'block_desktop' }}
            >
              <MobileImage
                {...getImageData(image as Asset)}
                loading="eager"
                heroImage={true}
              />
            </NoOverflowBox>
            <HeroText>
              <Layout margin={{ 0: ['ml', 0] }} gutter={{ 0: 'ml' }}>
                <Column size={{ 0: 10 }} flexDirection="column">
                  <Text
                    as="h1"
                    fontWeight="h1"
                    fontSize={{ 0: 'h1_0', tablet: 'h1_tablet' }}
                    colour={{
                      0: 'defaultText',
                      desktop: useLightText ? 'white' : 'defaultText'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: headingText || ''
                    }}
                  />
                  <Box marginTop="ml" marginBottom="ml">
                    <Text
                      as="p"
                      fontSize={{ 0: 'p_0', tablet: 'p_desktop' }}
                      fontWeight="p"
                    >
                      {subheadingText}
                    </Text>
                  </Box>
                  <AffiliatePhoneNumberWrapper
                    data-elementid="phone-wrapper-mobile"
                    margin="auto"
                  >
                    <CtaWidget
                      errorPosition="bottom"
                      cfButtonColor={(ctaColor as CFColorTokenProp) || 'ginger'}
                      cfButtonHoverColor={
                        (ctaHoverColor as CFColorTokenProp) || 'sweetmint'
                      }
                      id="homepage-hero-phone-number-mobile"
                      sysId="nonContentfulCta"
                      module="Hero"
                      data-elementid="phone-widget-mobile"
                      buttonContent="Get started"
                      sendConsentEvent={true}
                      designType={ctaWidgetEnum.INBLOCK_PHONE_NUMBER_V1}
                      placeholderText={DEFAULT_PLACEHOLDER_V1}
                      labelText={DEFAULT_LABEL_V1}
                      width="100%"
                    />
                  </AffiliatePhoneNumberWrapper>
                  {disclaimer && (
                    <Box marginTop="sm">
                      <RichText
                        data={disclaimer}
                        overrideOptions={RICH_TEXT_OVERRIDE}
                      />
                    </Box>
                  )}
                </Column>
              </Layout>
            </HeroText>
          </LayoutContainer>
        </BlockContainer>
      </MobileVersion>

      <DesktopBlockContainer
        as="section"
        marginTop={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
        marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
        revealOnScroll={false}
        data-blockid="affiliate-hero-desktop"
      >
        <DesktopHeroLayout
          alignItems="center"
          gutter={0}
          bgColor={'FFF'}
          bgImage={`https:${desktopImage?.fields?.file?.url}`}
          data-elementid="affiliate-hero-desktop-background"
        >
          <Column size={10}>
            <Flexbox
              flexDirection="column"
              justifyContent="center"
              flex="0 0 30rem"
            >
              <Box marginBottom="ml" marginTop={{ desktop: '4xl', max: '5xl' }}>
                <Text
                  as="h1"
                  fontWeight="h1"
                  fontSize={{ 0: 'h1_tablet' }}
                  colour={useLightText ? 'white' : 'defaultText'}
                  textAlign="left"
                  data-elementid="affiliate-hero-headline"
                  data-testid="affiliate-hero-headline"
                  dangerouslySetInnerHTML={{
                    __html: headingText || ''
                  }}
                />
              </Box>
              <Box marginBottom="ml">
                <SubheadText
                  as="h2"
                  fontWeight="p"
                  desktopSubheadingSize={'1.75'}
                  colour={useLightText ? 'white' : 'defaultText'}
                  textAlign="left"
                  data-elementid="homepage-hero-subhead"
                >
                  {subheadingText}
                </SubheadText>
              </Box>
              <Flexbox flexDirection="row">
                <HomepagePhoneNumberWrapper
                  data-elementid="phone-number-wrapper-desktop"
                  data-testid="phone-number-wrapper-desktop"
                >
                  <CtaWidget
                    errorPosition="bottom"
                    id="homepage-hero-phone-number-desktop"
                    module="Hero"
                    cfButtonColor={
                      (ctaDesktopColor as CFColorTokenProp) || 'green'
                    }
                    cfButtonHoverColor={
                      (ctaDesktopHoverColor as CFColorTokenProp) || 'sweetmint'
                    }
                    buttonContent="Get started"
                    sendConsentEvent={true}
                    sysId="nonContentfulCta"
                    designType={ctaWidgetEnum.INBLOCK_PHONE_NUMBER_V1}
                    placeholderText={DEFAULT_PLACEHOLDER_V1}
                    labelText={DEFAULT_LABEL_V1}
                    width="100%"
                  />
                </HomepagePhoneNumberWrapper>
              </Flexbox>
              <Box marginTop="l" marginBottom={{ desktop: '4xl', max: '5xl' }}>
                {disclaimer && (
                  <RichText
                    data={disclaimer}
                    overrideOptions={RICH_TEXT_OVERRIDE}
                  />
                )}
              </Box>
            </Flexbox>
          </Column>
        </DesktopHeroLayout>
      </DesktopBlockContainer>
    </>
  );
}
